import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import ChatWindowFooter from './components/chat-window-footer';
import ChatWindowHeader from './components/chat-window-header';
import ChatWindowEnd from './components/chat-window-end';
import ChatWindowTrigger from './components/chat-window-trigger';
import ChatWindowContent from './components/chat-window-content';
import { type BubbleComponentType } from '../bubble/bubble';
import {
	QueryClient,
	QueryClientProvider,
	UseMutationResult,
	useMutation,
} from 'react-query';
import {
	createChatBubble,
	createChatBubbleLoader,
	lockTabbing,
	tabIndexHandler,
	updateConversation,
} from './fn/chat-bubble-fns';
import { type AppConfig, ComponentType } from '@/lib/types';
import { cn, isColorDark } from '@/lib/utils';
import { ConfigContext, ConfigProvider } from '@/config/config-context';
import usePublicApi from '@/api/usePublicApi';
import DivWithInert from '../div-with-inert';

const queryClient = new QueryClient();

export type SourcesType = {
	title?: string;
	url: string;
};

export type ConversationType = {
	conversationIdentifier: string;
	message: string;
	adviceHumanExpertise: boolean;
	sources: SourcesType[];
};

export type ChatMutationType = UseMutationResult<
	ConversationType | null,
	Error,
	string
>;

export type ChatWindowSourceType = { open: boolean; index?: number };

export const ChatWindow = () => {
	const config = useContext(ConfigContext);
	const { postMessage } = usePublicApi();

	if (!config) {
		throw new Error('ChatWindow must be configured!');
	}

	const [chatWindowSources, setChatWindowSources] =
		useState<ChatWindowSourceType>({
			open: false,
			index: undefined,
		});
	const chatWindowSourcesRef = useRef<HTMLDivElement | null>(null);
	const chatWindowEndRef = useRef<HTMLDivElement | null>(null);
	const chatWindowFooterRef = useRef<HTMLDivElement | null>(null);
	const [expanded, setExpanded] = useState<boolean>(false);
	const [animate, setAnimate] = useState<boolean>(false);
	const [chatWindowEnd, setChatWindowEnd] = useState<boolean>(false);
	const [chatWindowExpanded, setChatWindowExpanded] = useState<boolean>(false);
	const [humanHandover, setHumanHandover] = useState<boolean>(false);
	const [inputValue, setInputValue] = useState<string | undefined>(undefined);
	const [conversation, setConversation] = useState<BubbleComponentType[]>([]);
	const chatWindowRef = useRef<HTMLDivElement | null>(null);

	// Restart conversation
	const handleEndConversation = () => {
		setConversation([]), setChatWindowEnd(false);
		setHumanHandover(false),
			setChatWindowSources({ open: false, index: undefined });
	};

	// Prevents inputs/buttons to be clicked while in loading state
	const isDisabled: boolean = useMemo(() => {
		if (conversation.some((item) => item.props.loading)) return true;
		return false;
	}, [conversation]);

	// Handles new chats between User & Chatbot
	const handleConversation = async (
		inputValue: string
	): Promise<ConversationType | null> => {
		if (inputValue.length === 0 || !inputValue) return null;

		if (chatWindowSources.open)
			setChatWindowSources({ open: false, index: undefined });

		const bubbleObj: BubbleComponentType = createChatBubble({
			type: ComponentType.Bubble,
			props: {
				user: true,
				loading: false,
				message: inputValue as string,
				status: '200',
			},
		});

		setConversation((prev) => [...prev, bubbleObj]);
		setInputValue('');

		createChatBubbleLoader({ setConversation });
		try {
			const data = await postMessage(inputValue);
			if (data) {
				return data;
			} else return null;
		} catch (error) {
			throw new Error('Error sending message');
		}
	};

	// Regenerates an answer and clears chat history from that index
	const handleRefreshConversation = async (
		value: string
	): Promise<ConversationType | null> => {
		if (humanHandover) setHumanHandover(false);
		const lastQuestion = conversation[parseInt(value) - 1];
		setConversation((prev) => {
			const parsedIndex = parseInt(value);

			if (parsedIndex > 0 && parsedIndex <= prev.length) {
				// Keep only the elements before the specified index
				const newConversation = prev.slice(0, parsedIndex);
				return newConversation;
			}

			// If the index is out of bounds, return the previous state
			return prev;
		});

		setConversation((prev) => [...prev]);
		setInputValue('');
		createChatBubbleLoader({ setConversation });
		try {
			const data = await postMessage(lastQuestion.props.message);
			if (data) {
				return data;
			} else return null;
		} catch (error) {
			throw new Error('Error sending message');
		}
	};

	const refreshMutation: ChatMutationType = useMutation({
		mutationFn: (value) => handleRefreshConversation(value),
		onSuccess: (data) => {
			if (!data) return;
			const answerObj: BubbleComponentType = createChatBubble({
				type: ComponentType.Bubble,
				props: {
					loading: false,
					message: data?.message ?? '',
					status: '200',
					user: false,
					humanHandover: data.adviceHumanExpertise,
					sources: data?.sources,
				},
			});
			updateConversation(answerObj, setConversation, setHumanHandover);
		},
		onError: (error: Error) => {
			const errorObj: BubbleComponentType = createChatBubble({
				type: ComponentType.Bubble,
				props: {
					loading: false,
					message: error.message ?? '',
					status: '500',
					user: false,
					humanHandover: false,
				},
			});
			updateConversation(errorObj, setConversation, setHumanHandover);
		},
	});

	const mutation: ChatMutationType = useMutation({
		mutationFn: (value) => handleConversation(value),
		onSuccess: (data) => {
			if (!data) return;
			const answerObj: BubbleComponentType = createChatBubble({
				type: ComponentType.Bubble,
				props: {
					loading: false,
					message: data?.message ?? '',
					status: '200',
					user: false,
					humanHandover: data.adviceHumanExpertise,
					sources: data?.sources,
				},
			});
			updateConversation(answerObj, setConversation, setHumanHandover);
		},
		onError: (error: Error) => {
			const errorObj: BubbleComponentType = createChatBubble({
				type: ComponentType.Bubble,
				props: {
					loading: false,
					message: error.message ?? '',
					status: '500',
					user: false,
					humanHandover: false,
				},
			});
			updateConversation(errorObj, setConversation, setHumanHandover);
		},
	});

	// Handles the removal of the loading bubble --
	useEffect(() => {
		if (mutation.isLoading || refreshMutation.isLoading) return;

		// Remove all loading bubbles when loading is false
		setConversation((prev) => prev.filter((bubble) => !bubble.props.loading));
	}, [refreshMutation.isLoading, mutation.isLoading]);

	// Handles welcome message from Chat -- Not an actual Chatbot message --
	useEffect(() => {
		if (chatWindowRef.current && expanded) {
			chatWindowRef.current.focus();
		}
		if (conversation.length === 0 && expanded) {
			const startingConversation: BubbleComponentType = createChatBubble({
				type: ComponentType.Bubble,
				props: {
					user: false,
					loading: true,
					message: `<p>${config?.welcomeMessage ?? 'Hej, vad kan jag hjälpa till med?'}</p>`,
					status: '200',
				},
				firstItem: true,
			});

			updateConversation(
				startingConversation,
				setConversation,
				setHumanHandover
			);
		}
	}, [conversation.length, expanded, config]);

	// Prevents tabbing to flowcontent beneath Source modal when open
	useEffect(() => {
		if (chatWindowSources.open) {
			return tabIndexHandler(true);
		} else tabIndexHandler(false);
	}, [chatWindowSources.open]);

	// Prevents the trigger to animate more than onLoad
	useEffect(() => {
		if (expanded) setAnimate(true);
	}, [expanded]);

	// Focuses on the chat window when expanded && locks the tabbing
	useEffect(() => {
		const activeRef =
			chatWindowEnd && chatWindowEndRef.current
				? chatWindowEndRef
				: chatWindowSources.open && chatWindowSourcesRef.current
					? chatWindowSourcesRef
					: chatWindowRef;

		if (!expanded || !activeRef.current) return;

		// Auto-focus when switching back to the main chat window
		if (
			expanded &&
			!chatWindowEnd &&
			!chatWindowSources.open &&
			activeRef.current !== document.activeElement
		) {
			activeRef.current.focus();
		}

		const cleanup = lockTabbing(activeRef);
		return cleanup;
	}, [expanded, chatWindowEnd, chatWindowSources.open]);

	return (
		<div
			id='chat-window-body'
			className={cn(
				'wlcb-flex wlcb-flex-col wlcb-items-end wlcb-justify-end wlcb-gap-y-2 wlcb-fixed md:wlcb-bottom-10 md:wlcb-right-10 wlcb-right-4 wlcb-bottom-4 ',
				{
					'wlcb-animate-slide-in-right': !expanded && !animate,
				}
			)}
		>
			{expanded ? (
				<div
					ref={chatWindowRef}
					tabIndex={-1}
					id='chat-window'
					className={cn(
						`${
							config?.theme?.primary?.color
								? isColorDark({
										color: config?.theme?.primary?.color,
									})
									? ''
									: 'wlcb-border wlcb-border-gray-200'
								: 'wlcb-border wlcb-border-gray-200'
						} wlcb-relative wlcb-flex wlcb-flex-col wlcb-w-[90vw] wlcb-items-start wlcb-justify-start wlcb-h-[calc(80dvh-60px)] wlcb-shadow-xl wlcb-rounded-sm wlcb-overflow-hidden focus-visible:wlcb-ring-0 focus-visible:wlcb-outline-0 focus-visible:wlcb-ring-transparent`,
						{
							'md:wlcb-min-w-[375px] md:wlcb-max-w-[25vw]': !chatWindowExpanded,
							'md:wlcb-min-w-[693px] md:wlcb-max-w-[50vw]': chatWindowExpanded,
						}
					)}
				>
					<DivWithInert inert={chatWindowSources.open}>
						<ChatWindowHeader
							chatWindowEnd={chatWindowEnd}
							setChatWindowEnd={setChatWindowEnd}
							setChatWindowExpanded={setChatWindowExpanded}
						/>
					</DivWithInert>
					<ChatWindowContent
						chatWindowSourcesRef={chatWindowSourcesRef}
						footerRef={chatWindowFooterRef}
						chatWindowExpanded={chatWindowExpanded}
						chatWindowEnd={chatWindowEnd}
						conversation={conversation}
						refresh={refreshMutation}
						setChatWindowSources={setChatWindowSources}
						chatWindowSources={chatWindowSources}
					/>
					<ChatWindowFooter
						ref={chatWindowFooterRef}
						chatWindowEnd={chatWindowEnd}
						humanHandover={humanHandover}
						setHumanHandover={setHumanHandover}
						setInputValue={setInputValue}
						inputValue={inputValue}
						mutation={mutation}
						isDisabled={isDisabled}
						conversationLength={conversation.length}
					/>
					{chatWindowEnd ? (
						<ChatWindowEnd
							ref={chatWindowEndRef}
							handleEndConversation={handleEndConversation}
							chatWindowEnd={chatWindowEnd}
							setChatWindowEnd={setChatWindowEnd}
							setExpanded={setExpanded}
						/>
					) : null}
				</div>
			) : null}
			<ChatWindowTrigger
				expanded={expanded}
				setExpanded={setExpanded}
				setChatWindowEnd={setChatWindowEnd}
			/>
		</div>
	);
};

/**
 * WrappedChatWindow component.
 *
 * @param {Object} props - The component props.
 * @param {AppConfig} props.config - The application configuration.
 * @param {string} [props.config.title] - The title of the application. [Optional]
 * @param {string} [props.config.chatBotName] - The name of the chatbot. [Optional]
 * @param {string} [props.config.chatBotAvatar] - The URL of the chatbot's avatar image. [Optional]
 * @param {string} [props.config.minimizedIcon] - The URL of the icon when the chat is minimized. [Optional]
 * @param {string} [props.config.welcomeMessage] - The welcome message displayed in the chat. [Optional]
 * @param {string} [props.config.minimizedChatText] - The text displayed when the chat is minimized. [Optional]
 * @param {string} props.config.baseUrl - The base URL for API requests. [Required]
 * @param {string} props.config.humanHandOverUrl - The URL for human handovers. [Required]
 * @param {Object} [props.config.theme] - The theme configuration object. [Optional]
 * @param {string} [props.config.theme.font] - The font used in the chat. [Optional]
 * @param {Object} [props.config.theme.primary] - The primary theme colors. [Optional]
 * @param {string} [props.config.theme.primary.color] - The primary color. [Required if Primary Theme]
 * @param {string} [props.config.theme.primary.textColor] - The text color for the primary theme. [Required if Primary Theme]
 * @param {Object} [props.config.theme.secondary] - The secondary theme colors. [Optional]
 * @param {string} [props.config.theme.secondary.color] - The secondary color. [Required if Secondary Theme]
 * @param {string} [props.config.theme.secondary.textColor] - The text color for the secondary theme. [Required if Secondary Theme]
 * @returns {JSX.Element} The rendered component.
 */
const WrappedChatWindow = ({ config }: { config: AppConfig }): JSX.Element => {
	// If config comes from data-attributes --
	const wlcbRoot = document.getElementById('wlcb-root');

	const configFromAttribute = wlcbRoot?.getAttribute('data-wlcb-config');
	const wlcbConfig: AppConfig = configFromAttribute
		? JSON.parse(configFromAttribute)
		: config;

	return (
		<QueryClientProvider client={queryClient}>
			<ConfigProvider appConfig={wlcbConfig}>
				<ChatWindow />
			</ConfigProvider>
		</QueryClientProvider>
	);
};

export default WrappedChatWindow;
